import { useEffect, useMemo, useState } from "react";
import type { Column } from "../components/DataTable";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import { useBusy } from "../../context/BusyContext";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import "../styles/adminMarketAnalysisResults.css";

type MarketResultRow = {
  id: number;
  runId: number;
  subscriberId: number;
  title: string;
  sellerName: string | null;
  domain: string | null;
  url: string | null;
  priceValue: number | null;
  originalPriceValue: number | null;
  originalCurrency: string | null;
  conversionRate: number | null;
  currency: string | null;
  rating: number | null;
  reviewsCount: number | null;
  rankPosition: number | null;
  matchScore: number | null;
  matchType: "Exact" | "Similar" | "Weak" | "Unmatched";
  rawJson: string | null;
  createdAt: string;

  searchKeyword: string | null;
  locationName: string | null;
  languageCode: string | null;
  accessType: string | null;
  chargedAmount: number | null;
  runStatus: string | null;
  lowestPrice: number | null;
  averagePrice: number | null;
  highestPrice: number | null;
  resultCount: number | null;
  runCreatedAt: string | null;
  runCompletedAt: string | null;

  subscriberName: string | null;
  subscriberEmail: string | null;
  subscriberPhone: string | null;
};

export default function AdminMarketAnalysisResults() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();
  const { runBusy } = useBusy();

  const [data, setData] = useState<MarketResultRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<MarketResultRow | null>(null);
  const [activeRow, setActiveRow] = useState<MarketResultRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [serverSearch, setServerSearch] = useState("");
  const [resultCount, setResultCount] = useState(0);

  const formatMoney = (
    value?: number | string | null,
    currencyCode = "MYR",
  ) => {
    const amount = Number(value || 0);

    return `${String(currencyCode || "MYR").toUpperCase()} ${amount.toLocaleString(
      "en-MY",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )}`;
  };

  const fetchResults = async (searchValue = "") => {
    await runBusy(
      async () => {
        try {
          const query = new URLSearchParams();

          if (searchValue.trim()) {
            query.set("search", searchValue.trim());
          }

          const res = await fetch(
            `${BASE}/admin/market-analysis-results${
              query.toString() ? `?${query.toString()}` : ""
            }`,
            {
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            const freshData = Array.isArray(result.data) ? result.data : [];

            setData(freshData);
            setResultCount(Number(result.total || freshData.length || 0));

            setSelectedRow((prev) =>
              prev
                ? freshData.find((item: any) => item.id === prev.id) || null
                : null,
            );
          } else {
            setData([]);
            setResultCount(0);

            showModal({
              type: "error",
              title: "Error",
              message:
                result.message || "Failed to fetch Market Analysis results.",
            });
          }
        } catch (error) {
          console.error("Fetch Market Analysis Results Error:", error);

          setData([]);
          setResultCount(0);

          showModal({
            type: "error",
            title: "Error",
            message: "Failed to fetch Market Analysis results.",
          });
        }
      },
      "Please wait...",
      "spinner",
    );
  };

  useEffect(() => {
    fetchResults("");
  }, []);

  const openViewModal = (row: MarketResultRow) => {
    setActiveRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setActiveRow(null);
    setModalOpen(false);
  };

  const getSourceLink = (row: MarketResultRow) => {
    if (!row.url) return "-";

    return (
      <a
        href={row.url}
        target="_blank"
        rel="noreferrer"
        className="market-result-source-link"
        onClick={(event) => event.stopPropagation()}
      >
        {row.domain || "Open Source"}
      </a>
    );
  };

  const renderMatchBadge = (value: string | null) => {
    const normalized = String(value || "Unmatched").toLowerCase();

    const className =
      normalized === "exact"
        ? "badge-success"
        : normalized === "similar"
          ? "badge-warning"
          : normalized === "weak"
            ? "badge-secondary"
            : "badge-danger";

    return <span className={className}>{value || "Unmatched"}</span>;
  };

  const tableRows = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        sourceDisplay: row.domain || "-",
        convertedPriceDisplay: formatMoney(
          row.priceValue,
          row.currency || "MYR",
        ),
        originalPriceDisplay: row.originalPriceValue
          ? formatMoney(
              row.originalPriceValue,
              row.originalCurrency || row.currency || "MYR",
            )
          : "-",
        searchDisplay: row.searchKeyword || "-",
        marketDisplay: row.locationName || "-",
        subscriberDisplay:
          row.subscriberName ||
          row.subscriberEmail ||
          `Subscriber #${row.subscriberId}`,
        createdAtDisplay: row.createdAt ? formatDateToDMY(row.createdAt) : "-",
      })),
    [data, formatDateToDMY],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "searchDisplay", label: "Search Keyword" },
    { key: "title", label: "Market Product" },
    { key: "sellerName", label: "Seller" },
    { key: "marketDisplay", label: "Market" },
    {
      key: "priceValue",
      label: "Compare Price",
      render: (row: MarketResultRow) =>
        formatMoney(row.priceValue, row.currency || "MYR"),
    },
    {
      key: "originalPriceValue",
      label: "Source Price",
      render: (row: MarketResultRow) =>
        row.originalPriceValue
          ? formatMoney(
              row.originalPriceValue,
              row.originalCurrency || row.currency || "MYR",
            )
          : "-",
    },
    {
      key: "matchType",
      label: "Match",
      render: (row: MarketResultRow) => renderMatchBadge(row.matchType),
    },
    {
      key: "matchScore",
      label: "Score",
      render: (row: MarketResultRow) => `${Number(row.matchScore || 0)}%`,
    },
    {
      key: "source",
      label: "Source",
      render: (row: MarketResultRow) => getSourceLink(row),
    },
    {
      key: "createdAt",
      label: "Created At",
      render: (row: MarketResultRow) =>
        row.createdAt ? formatDateToDMY(row.createdAt) : "-",
    },
  ];

  return (
    <div className="admin-market-results-page">
      <div className="market-results-server-search">
        <div className="market-results-search-field">
          <label>
            Search All Market Results{" "}
            <span className="market-results-count">
              - [ {resultCount} records found ]
            </span>
          </label>

          <input
            value={serverSearch}
            placeholder="Search product, seller, source, country, subscriber..."
            onChange={(event) => setServerSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                fetchResults(serverSearch);
              }
            }}
          />
        </div>

        <div className="market-results-search-actions">
          <button
            type="button"
            className="market-results-apply-btn"
            onClick={() => fetchResults(serverSearch)}
          >
            Apply
          </button>

          <button
            type="button"
            className="market-results-clear-btn"
            onClick={() => {
              setServerSearch("");
              fetchResults("");
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <p className="market-results-helper-text">
        Latest 20 records are shown initially. When you search, all relevant
        matching records are fetched.
      </p>

      <AdminListToolkit
        title="Market Analysis Results"
        columns={columns}
        data={tableRows}
        selectedRow={selectedRow}
        onView={(row) => openViewModal(row as MarketResultRow)}
        enableExport
        exportFileName="market-analysis-results"
      >
        {(filteredData) => (
          <div className="market-results-table-wrap">
            <DataTable
              columns={columns}
              data={filteredData}
              selectedRowId={selectedRow?.id}
              onRowSelect={(row) => setSelectedRow(row as MarketResultRow)}
              getRowClassName={(row) =>
                selectedRow?.id === row.id ? "market-results-selected-row" : ""
              }
            />
          </div>
        )}
      </AdminListToolkit>

      {modalOpen && activeRow && (
        <div className="market-result-modal-overlay">
          <div className="market-result-modal">
            <div className="market-result-modal-header">
              <h3>View Market Analysis Result</h3>

              <button type="button" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="market-result-modal-body">
              <div className="market-result-detail-grid">
                <div>
                  <label>Result ID</label>
                  <p>{activeRow.id}</p>
                </div>

                <div>
                  <label>Run ID</label>
                  <p>{activeRow.runId}</p>
                </div>

                <div>
                  <label>Subscriber</label>
                  <p>
                    {activeRow.subscriberName ||
                      activeRow.subscriberEmail ||
                      `#${activeRow.subscriberId}`}
                  </p>
                </div>

                <div>
                  <label>Subscriber Email</label>
                  <p>{activeRow.subscriberEmail || "-"}</p>
                </div>

                <div>
                  <label>Search Keyword</label>
                  <p>{activeRow.searchKeyword || "-"}</p>
                </div>

                <div>
                  <label>Market Country</label>
                  <p>{activeRow.locationName || "-"}</p>
                </div>

                <div className="full">
                  <label>Market Product</label>
                  <p>{activeRow.title || "-"}</p>
                </div>

                <div>
                  <label>Seller</label>
                  <p>{activeRow.sellerName || "-"}</p>
                </div>

                <div>
                  <label>Domain</label>
                  <p>{activeRow.domain || "-"}</p>
                </div>

                <div>
                  <label>Compare Price</label>
                  <p>
                    {formatMoney(
                      activeRow.priceValue,
                      activeRow.currency || "MYR",
                    )}
                  </p>
                </div>

                <div>
                  <label>Source Price</label>
                  <p>
                    {activeRow.originalPriceValue
                      ? formatMoney(
                          activeRow.originalPriceValue,
                          activeRow.originalCurrency ||
                            activeRow.currency ||
                            "MYR",
                        )
                      : "-"}
                  </p>
                </div>

                <div>
                  <label>Conversion Rate</label>
                  <p>
                    {activeRow.conversionRate
                      ? Number(activeRow.conversionRate).toFixed(8)
                      : "-"}
                  </p>
                </div>

                <div>
                  <label>Match</label>
                  <p>{activeRow.matchType || "-"}</p>
                </div>

                <div>
                  <label>Score</label>
                  <p>{Number(activeRow.matchScore || 0)}%</p>
                </div>

                <div>
                  <label>Rating</label>
                  <p>{activeRow.rating || "-"}</p>
                </div>

                <div>
                  <label>Reviews</label>
                  <p>{activeRow.reviewsCount || "-"}</p>
                </div>

                <div>
                  <label>Rank</label>
                  <p>{activeRow.rankPosition || "-"}</p>
                </div>

                <div>
                  <label>Created At</label>
                  <p>
                    {activeRow.createdAt
                      ? new Date(activeRow.createdAt).toLocaleString()
                      : "-"}
                  </p>
                </div>

                <div className="full">
                  <label>Source URL</label>
                  <p>
                    {activeRow.url ? (
                      <a href={activeRow.url} target="_blank" rel="noreferrer">
                        {activeRow.url}
                      </a>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>

                <div className="full">
                  <label>Raw JSON</label>
                  <pre>{activeRow.rawJson || "-"}</pre>
                </div>
              </div>
            </div>

            <div className="market-result-modal-footer">
              <button type="button" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
